
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

// UI_BUILD_SPEC §1.2.11. Tags are user data, not state, so they are deliberately
// neutral — the previous brand-blue chip made tags compete with status pills for
// the eye in every queue row.

interface TagProps {
  label: string;
  onRemove?: () => void;
  className?: string;
}

export function Tag({ label, onRemove, className }: TagProps) {
  const { t } = useI18n();
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-full bg-surface-subtle pl-2.5 text-xs text-ink-muted',
        onRemove ? 'pr-1' : 'pr-2.5',
        className,
      )}
    >
      <span className="max-w-[20ch] truncate">{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('ui.removeTag', { label })}
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <X className="h-2.5 w-2.5" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

/**
 * Caps visible tags and summarises the rest (§1.2.11) so a heavily tagged
 * ticket cannot blow out a fixed-height queue row.
 */
export function TagList({ tags, max, className }: { tags: string[]; max?: number; className?: string }) {
  if (tags.length === 0) return null;
  const visible = max ? tags.slice(0, max) : tags;
  const hidden = tags.length - visible.length;
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1.5', className)}>
      {visible.map((tag) => <Tag key={tag} label={tag} />)}
      {hidden > 0 && (
        <span className="text-xs text-ink-subtle" title={tags.slice(visible.length).join(', ')}>
          +{hidden}
        </span>
      )}
    </span>
  );
}
